/**
 * Location utility functions
 * Helper functions for extracting and processing location data
 */

/**
 * Extract 2-letter state code from location object or string
 * @param {Object|string} location - Location object with formatted_address, address, or location string
 * @returns {string|null} - Uppercase 2-letter state code (e.g., "GA", "CA") or null if not found
 */
export function extractStateFromLocation(location) {
  if (!location) return null;

  let addressString = null;

  // Handle different location formats
  if (typeof location === 'string') {
    addressString = location;
  } else if (location.formatted_address) {
    addressString = location.formatted_address;
  } else if (location.address) {
    addressString = location.address;
  } else if (location.location) {
    addressString = location.location;
  }

  if (!addressString) return null;

  // Extract state code using common address patterns
  // Patterns: ", GA ", ", GA", " GA ", " GA" (at end)
  // This matches the pattern used in Camp.byState()
  const statePatterns = [
    /,\s+([A-Z]{2})\s+/,      // ", GA " (comma, space, state, space)
    /,\s+([A-Z]{2})$/,        // ", GA" (comma, space, state at end)
    /\s+([A-Z]{2})\s+/,       // " GA " (space, state, space)
    /\s+([A-Z]{2})$/,         // " GA" (space, state at end)
  ];

  for (const pattern of statePatterns) {
    const match = addressString.match(pattern);
    if (match && match[1]) {
      return match[1].toUpperCase();
    }
  }

  // Fallback: try to extract from comma-separated parts
  // Format: "City, STATE ZIP" or "City, STATE"
  const parts = addressString.split(',');
  if (parts.length >= 2) {
    const lastPart = parts[parts.length - 1].trim();
    // Extract state code (2 uppercase letters) from last part
    const stateMatch = lastPart.match(/\b([A-Z]{2})\b/);
    if (stateMatch && stateMatch[1]) {
      return stateMatch[1].toUpperCase();
    }
  }

  return null;
}
